import produce, { enableMapSet } from "immer";
import { useReducer } from "react";
import { CompletedTodo } from "shared/todos/CompletedTodo";
import { Todo } from "shared/todos/Todo";
import mergeSets from "util/mergeSets";
import useCompletedTodoLocaleStorageSync, {
  getSignedOutCompletedTodosFromStorage,
} from "./useCompletedTodoLocaleStorageSync";

enableMapSet();

/**
 * Types
 */

export type CompletedTodoSet = Set<number>;

export interface State {
  todos: Todo[];
  state: "loading" | "loaded";
  completedTodos: {
    signedIn: CompletedTodoSet;
    signedOut: CompletedTodoSet;
  };
}

/**
 * Actions
 */

interface ActionSetCompletedTodos {
  type: "SET_MULTIPLE_SIGNED_IN_COMPLETED_TODO";
  payload: {
    completedTodos: CompletedTodo[];
  };
}

interface ActionSetCompletedTodosSignedOut {
  type: "SET_SINGLE_SIGNED_OUT_COMPLETED_TODO";
  payload: {
    completedTodoId: number;
  };
}

interface ActionMoveSignedOutCompletedTodosToSignedIn {
  type: "MOVE_SIGNED_OUT_COMPLETED_TODOS_TO_SIGNED_IN";
  payload: {
    completedTodos: CompletedTodo[];
  };
}

interface ActionLoadingComplete {
  type: "LOADING_COMPLETE";
}

export type Action =
  | ActionSetCompletedTodos
  | ActionLoadingComplete
  | ActionSetCompletedTodosSignedOut
  | ActionMoveSignedOutCompletedTodosToSignedIn;

/**
 * Selectors
 */

export function selectCompletedTodos(state: State): CompletedTodoSet {
  return mergeSets(Object.values(state.completedTodos));
}

/**
 * Reducer
 */

interface InitializerProps {
  todos: Todo[];
}

function initaliazer({ todos }: InitializerProps): State {
  return {
    state: "loading",
    todos,
    completedTodos: {
      signedIn: new Set(),
      signedOut: getSignedOutCompletedTodosFromStorage(),
    },
  };
}

function reducer(state: State, action: Action): State {
  return produce(state, (draft) => {
    if (action.type === "LOADING_COMPLETE") {
      draft.state = "loaded";

      return draft;
    }

    if (action.type === "SET_MULTIPLE_SIGNED_IN_COMPLETED_TODO") {
      action.payload.completedTodos.forEach(({ todo_id }) => {
        draft.completedTodos.signedIn.add(todo_id);
      });
      draft.state = "loaded";

      return draft;
    }

    if (action.type === "SET_SINGLE_SIGNED_OUT_COMPLETED_TODO") {
      draft.completedTodos.signedOut.add(action.payload.completedTodoId);

      return draft;
    }

    if (action.type === "MOVE_SIGNED_OUT_COMPLETED_TODOS_TO_SIGNED_IN") {
      action.payload.completedTodos.forEach(({ todo_id }) => {
        draft.completedTodos.signedIn.add(todo_id);
      });

      draft.completedTodos.signedOut.clear();

      return draft;
    }
  });
}

/**
 * Hook
 */

export default function useTodosReducer(props: InitializerProps) {
  const value = useReducer(reducer, props, initaliazer);

  useCompletedTodoLocaleStorageSync(value);

  return value;
}
